/**
 * @page Eingebettet in /performance (Tab-Komponente)
 * @description MA-Effizienz — Mitarbeiter-Effizienz-Ranking mit Netto/Brutto-Vergleich,
 *   Trend-Analyse und MA-Vergleichsmodus.
 * @features
 *   - Effizienz-Ranking sortierbar nach Netto/Brutto/Pakete
 *   - Filter nach Standort, Komplexitaetsklasse, Kunde, Mitarbeiter
 *   - Trend-Chart (Tag/Woche/Monat) mit Mitarbeiter-Filter
 *   - Vergleichsmodus: bis zu 4 MAs nebeneinander
 *   - Zeitvorgaben-Referenzlinie in Charts
 * @api GET /api/performance/ma-effizienz/ranking
 * @api GET /api/performance/ma-effizienz/warehouses
 * @api GET /api/performance/ma-effizienz/employees
 * @api GET /api/performance/ma-effizienz/merchants
 * @api GET /api/performance/ma-effizienz/trend
 * @api GET /api/performance/ma-effizienz/vergleich
 * @roles admin, manager
 * @i18n efficiency
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

const API = import.meta.env.VITE_API_URL || '';

const COLORS = { violet: '#338edc', cyan: '#22D3EE', indigo: '#6366F1', amber: '#F59E0B', gray: '#E5E7EB' };
const COMPARE_COLORS = [COLORS.violet, COLORS.cyan, COLORS.indigo, COLORS.amber];

function fmt(n) { return n == null ? '–' : Number(n).toLocaleString('de-DE'); }
function fmtD1(n) { return n == null ? '–' : Number(n).toFixed(1).replace('.', ','); }
function fmtD2(n) { return n == null ? '–' : Number(n).toFixed(2).replace('.', ','); }
function fmtEur(n) { return n == null ? '–' : Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'; }
function fmtH(n) { return n == null ? '–' : Number(n).toFixed(1).replace('.', ',') + ' h'; }
function fmtPeriod(p) {
  if (!p) return '';
  const d = new Date(p + 'T00:00:00');
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}`;
}

function barColor(relative) {
  if (relative > 120) return 'bg-emerald-500/20 dark:bg-emerald-500/15';
  if (relative < 80) return 'bg-amber-500/20 dark:bg-amber-500/15';
  return 'bg-gray-300/30 dark:bg-gray-500/15';
}

export default function EffizienzTab({ von, bis, type = 'pack' }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [klasseFilter, setKlasseFilter] = useState('all');
  const [merchantFilter, setMerchantFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [expandedMa, setExpandedMa] = useState(null);
  const [sortCol, setSortCol] = useState('netto_effizienz');
  const [sortDir, setSortDir] = useState('desc');
  const [trendData, setTrendData] = useState(null);
  const [granularity, setGranularity] = useState('week');
  const [trendEmployee, setTrendEmployee] = useState('');
  const [trendKlasse, setTrendKlasse] = useState('all');
  const [compareMode, setCompareMode] = useState(false);
  const [compareSelected, setCompareSelected] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [subView, setSubView] = useState('ranking'); // ranking | trend | compare

  const unitLabel = type === 'pick' ? t('efficiency.skuPerMin') : t('efficiency.artPerMin');

  // Complexity class labels based on type
  const klasseLabels = useMemo(() => {
    if (type === 'pick') {
      return [
        { key: 'all', label: t('efficiency.all') },
        { key: '1', label: t('efficiency.pick1') },
        { key: '2', label: t('efficiency.pick2') },
        { key: '3', label: t('efficiency.pick3') },
        { key: '4', label: t('efficiency.pick4') },
      ];
    }
    return [
      { key: 'all', label: t('efficiency.all') },
      { key: '1', label: t('efficiency.pack1') },
      { key: '2', label: t('efficiency.pack2') },
      { key: '3', label: t('efficiency.pack3') },
      { key: '4', label: t('efficiency.pack4') },
    ];
  }, [type, t]);

  const klasseChartNames = useMemo(() => {
    if (type === 'pick') return { 1: '1 SKU', 2: '2-5', 3: '6-15', 4: '16+' };
    return { 1: '1 Art', 2: '2-5', 3: '6-15', 4: '16+' };
  }, [type]);

  // Load warehouses
  useEffect(() => {
    fetch(`${API}/api/performance/ma-effizienz/warehouses`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : []).then(d => setWarehouses(d || [])).catch(() => {});
  }, []);

  // Load ranking data
  const loadRanking = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ von, bis, type, klasse: klasseFilter });
      if (merchantFilter) params.set('merchant', merchantFilter);
      if (employeeFilter) params.set('employee', employeeFilter);
      if (warehouseFilter) params.set('warehouse', warehouseFilter);
      const res = await fetch(`${API}/api/performance/ma-effizienz/ranking?${params}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [von, bis, type, klasseFilter, merchantFilter, employeeFilter, warehouseFilter]);

  // Load filter options
  useEffect(() => {
    if (!von || !bis) return;
    const params = new URLSearchParams({ von, bis, type });
    if (warehouseFilter) params.set('warehouse', warehouseFilter);
    fetch(`${API}/api/performance/ma-effizienz/employees?${params}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : []).then(d => setEmployees(d || [])).catch(() => {});
    fetch(`${API}/api/performance/ma-effizienz/merchants?${params}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : []).then(d => setMerchants(d || [])).catch(() => {});
  }, [von, bis, type, warehouseFilter]);

  useEffect(() => { if (von && bis) loadRanking(); }, [loadRanking]);

  // Load trend
  useEffect(() => {
    if (!von || !bis || subView !== 'trend') return;
    const params = new URLSearchParams({ von, bis, type, granularity });
    if (trendEmployee) params.set('employee', trendEmployee);
    if (trendKlasse !== 'all') params.set('klasse', trendKlasse);
    if (warehouseFilter) params.set('warehouse', warehouseFilter);
    fetch(`${API}/api/performance/ma-effizienz/trend?${params}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { trend: [] }).then(d => setTrendData(d.trend || [])).catch(() => setTrendData([]));
  }, [von, bis, type, granularity, trendEmployee, trendKlasse, subView, warehouseFilter]);

  // Load comparison
  useEffect(() => {
    if (!von || !bis || subView !== 'compare' || compareSelected.length < 2) { setCompareData(null); return; }
    const params = new URLSearchParams({ von, bis, type, employees: compareSelected.join(',') });
    if (warehouseFilter) params.set('warehouse', warehouseFilter);
    fetch(`${API}/api/performance/ma-effizienz/vergleich?${params}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { employees: [] }).then(d => setCompareData(d.employees || [])).catch(() => setCompareData([]));
  }, [von, bis, type, compareSelected, subView, warehouseFilter]);

  const sorted = useMemo(() => {
    if (!data?.ranking) return [];
    return [...data.ranking].sort((a, b) => {
      const av = a[sortCol], bv = b[sortCol];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
  }, [data, sortCol, sortDir]);

  const maxNetto = useMemo(() => {
    if (!data?.ranking?.length) return 1;
    return Math.max(...data.ranking.map(r => r.netto_effizienz), 0.01);
  }, [data]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  }

  function toggleCompare(name) {
    setCompareSelected(prev => {
      if (prev.includes(name)) return prev.filter(n => n !== name);
      if (prev.length >= 5) return prev;
      return [...prev, name];
    });
  }

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <span className="text-[var(--text-tertiary)] ml-0.5">↕</span>;
    return <span className="text-primary-500 ml-0.5">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  if (loading && !data) return <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" /></div>;
  if (!data?.ranking?.length) return <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('efficiency.noData')}</div>;

  const s = data.summary;

  return (
    <div>
      {/* Warehouse toggle */}
      {warehouses.length > 1 && (
        <div className="flex items-center gap-2 mb-3">
          <div className="flex bg-surface-inset rounded-lg p-0.5 border border-line-hover">
            <button onClick={() => setWarehouseFilter('')}
              className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${
                !warehouseFilter ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-[var(--text-secondary)] hover:text-content-secondary'
              }`}>
              {t('efficiency.allWarehouses')}
            </button>
            {warehouses.map(w => (
              <button key={w.name} onClick={() => setWarehouseFilter(w.name)}
                className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${
                  warehouseFilter === w.name ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-[var(--text-secondary)] hover:text-content-secondary'
                }`}>
                {w.short}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {/* Complexity class toggle */}
        <div className="flex bg-surface-inset rounded-lg p-0.5 border border-line-hover">
          {klasseLabels.map(f => (
            <button key={f.key} onClick={() => setKlasseFilter(f.key)}
              className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${
                klasseFilter === f.key ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-[var(--text-secondary)] hover:text-content-secondary'
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Merchant filter */}
        {merchants.length > 0 && (
          <select value={merchantFilter} onChange={e => setMerchantFilter(e.target.value)}
            className="bg-surface-inset border border-line-hover rounded-lg px-2.5 py-1 text-xs text-content focus:outline-none max-w-[180px]">
            <option value="">{t('performance.allCustomers')}</option>
            {merchants.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        )}

        {/* Employee filter */}
        {employees.length > 0 && (
          <select value={employeeFilter} onChange={e => setEmployeeFilter(e.target.value)}
            className="bg-surface-inset border border-line-hover rounded-lg px-2.5 py-1 text-xs text-content focus:outline-none max-w-[180px]">
            <option value="">{t('performance.allEmployees')}</option>
            {employees.map(e => <option key={e.name} value={e.name}>{e.name}{e.is_shared ? ' ⚙' : ''}</option>)}
          </select>
        )}
      </div>

      {/* 5 KPI Cards */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        <div className="text-center p-3 bg-surface-card border border-line rounded-card">
          <div className="text-[10px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider mb-1">{t('efficiency.bruttoEff')}</div>
          <div className="text-xl font-bold text-content">{fmtD1(s.team_brutto_avg)} <span className="text-xs font-normal text-[var(--text-secondary)]">{unitLabel}</span></div>
        </div>
        <div className="text-center p-3 bg-surface-card border border-line rounded-card">
          <div className="text-[10px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider mb-1">{t('efficiency.nettoEff')}</div>
          <div className="text-xl font-bold text-content">{fmtD1(s.team_netto_avg)} <span className="text-xs font-normal text-[var(--text-secondary)]">{unitLabel}</span></div>
        </div>
        <div className="text-center p-3 bg-surface-card border border-line rounded-card">
          <div className="text-[10px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider mb-1">{t('efficiency.totalArticles')}</div>
          <div className="text-xl font-bold text-content">{fmt(s.total_artikel)}</div>
        </div>
        {type === 'pack' && s.gesamt_gewicht_kg > 0 && (
          <div className="text-center p-3 bg-surface-card border border-line rounded-card">
            <div className="text-[10px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider mb-1">{t('performance.gesamtGewicht')}</div>
            <div className="text-xl font-bold text-content">{fmt(Math.round(s.gesamt_gewicht_kg))} <span className="text-xs font-normal text-[var(--text-secondary)]">kg</span></div>
          </div>
        )}
        <div className="text-center p-3 bg-surface-card border border-line rounded-card">
          <div className="text-[10px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider mb-1">{t('efficiency.outliersRemoved')}</div>
          <div className="text-xl font-bold text-content">{fmt(s.ausreisser_entfernt)}</div>
        </div>
      </div>

      {/* Complexity overview */}
      {data.klassen_uebersicht && (
        <div className="grid grid-cols-4 gap-3 mb-4">
          {data.klassen_uebersicht.map(ku => (
            <div key={ku.klasse} className="p-3 bg-surface-card border border-line rounded-card">
              <div className="text-[10px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider mb-1">{ku.label}</div>
              <div className="text-sm font-bold text-content">{fmt(ku.vorgaenge)} <span className="text-[var(--text-secondary)] font-normal text-xs">({fmtD1(ku.anteil_percent)}%)</span></div>
              <div className="text-[10px] text-[var(--text-secondary)]">{t('efficiency.teamAvg')}: {fmtD1(ku.team_avg_brutto)} {unitLabel}</div>
            </div>
          ))}
        </div>
      )}

      {/* Sub-view tabs */}
      <div className="flex gap-1 mb-4 border-b border-line">
        {[
          { key: 'ranking', label: t('efficiency.ranking') },
          { key: 'trend', label: t('efficiency.trend') },
          { key: 'compare', label: t('efficiency.compare') },
        ].map(tab => (
          <button key={tab.key} onClick={() => setSubView(tab.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              subView === tab.key ? 'text-content border-b-2 border-primary-500' : 'text-[var(--text-secondary)] hover:text-content-secondary'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Ranking Table ── */}
      {subView === 'ranking' && (
        <>
          <div className="text-[10px] text-[var(--text-tertiary)] mb-2">{t('efficiency.outliersNote')}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line">
                  <th className="px-2 py-2 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-center w-10">#</th>
                  <th className="px-2 py-2 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-left cursor-pointer" onClick={() => toggleSort('employee_name')}>{t('idleTime.employee')} <SortIcon col="employee_name" /></th>
                  <th className="px-2 py-2 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right cursor-pointer" onClick={() => toggleSort('vorgaenge')}>{t('efficiency.activities')} <SortIcon col="vorgaenge" /></th>
                  <th className="px-2 py-2 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right cursor-pointer" onClick={() => toggleSort('artikel_gesamt')}>{t('efficiency.articles')} <SortIcon col="artikel_gesamt" /></th>
                  <th className="px-2 py-2 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right cursor-pointer" onClick={() => toggleSort('brutto_effizienz')}>{t('efficiency.grossEff')} <SortIcon col="brutto_effizienz" /></th>
                  <th className="px-2 py-2 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right cursor-pointer" onClick={() => toggleSort('idle_minuten')}>{t('efficiency.idleTime')} <SortIcon col="idle_minuten" /></th>
                  <th className="px-2 py-2 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right cursor-pointer" onClick={() => toggleSort('netto_effizienz')}>{t('efficiency.netEff')} <SortIcon col="netto_effizienz" /></th>
                  <th className="px-2 py-2 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right cursor-pointer" onClick={() => toggleSort('relative_effizienz')}>{t('efficiency.relative')} <SortIcon col="relative_effizienz" /></th>
                  <th className="px-2 py-2 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right cursor-pointer" onClick={() => toggleSort('kosten_pro_artikel')}>{t('efficiency.costPerArticle')} <SortIcon col="kosten_pro_artikel" /></th>
                  <th className="px-2 py-2 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right">{t('efficiency.sop')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, idx) => (
                  <>
                    <tr key={r.employee_name}
                      className={`border-b border-line-subtle cursor-pointer transition-colors ${expandedMa === r.employee_name ? 'bg-[var(--bg-selected-row)]' : 'hover:bg-surface-hover'}`}
                      onClick={() => setExpandedMa(expandedMa === r.employee_name ? null : r.employee_name)}>
                      <td className="px-2 py-2 text-center text-[var(--text-secondary)] font-mono text-xs">{r.rank}</td>
                      <td className="px-2 py-2 text-left text-content font-medium">
                        {r.employee_name}
                        {r.is_shared_account && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">SHARED</span>}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-content">{fmt(r.vorgaenge)}</td>
                      <td className="px-2 py-2 text-right font-mono text-content">{fmt(r.artikel_gesamt)}</td>
                      <td className="px-2 py-2 text-right font-mono text-content">{fmtD1(r.brutto_effizienz)}</td>
                      <td className="px-2 py-2 text-right font-mono text-content">{fmtH(r.idle_minuten / 60)}</td>
                      {/* Netto cell with progress bar */}
                      <td className="px-2 py-2 text-right relative overflow-hidden">
                        <div className={`absolute inset-y-0 left-0 ${barColor(r.relative_effizienz)} transition-all`}
                          style={{ width: `${Math.min(100, (r.netto_effizienz / maxNetto) * 100)}%` }} />
                        <span className="relative font-mono font-bold text-content">{fmtD1(r.netto_effizienz)}</span>
                      </td>
                      {/* Relativ cell with progress bar */}
                      <td className="px-2 py-2 text-right relative overflow-hidden">
                        <div className={`absolute inset-y-0 left-0 ${barColor(r.relative_effizienz)} transition-all`}
                          style={{ width: `${Math.min(r.relative_effizienz, 200) / 2}%` }} />
                        <span className="relative font-mono text-content">{r.relative_effizienz >= 200 ? '200%+' : `${r.relative_effizienz}%`}</span>
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-content">{fmtEur(r.kosten_pro_artikel)}</td>
                      <td className="px-2 py-2 text-right font-mono text-[var(--text-secondary)]">{r.sop.packs > 0 ? fmt(r.sop.packs) : '–'}</td>
                    </tr>
                    {/* Expanded detail */}
                    {expandedMa === r.employee_name && (
                      <tr key={`${r.employee_name}-detail`}>
                        <td colSpan={10} className="px-4 py-4 bg-[var(--bg-inset)]">
                          <div className="text-xs font-semibold text-[var(--text-secondary)] mb-3 uppercase tracking-wider">{t('efficiency.complexityClass')} — {r.employee_name}</div>
                          <div className="h-48">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={[1, 2, 3, 4].map(k => ({
                                name: klasseChartNames[k],
                                ma: r.klassen_verteilung[k]?.brutto_eff || 0,
                                team: r.klassen_verteilung[k]?.team_avg || 0,
                                vorgaenge: r.klassen_verteilung[k]?.vorgaenge || 0,
                              }))} barGap={4}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-table)" />
                                <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                                <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} />
                                <Tooltip
                                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: 8, fontSize: 12 }}
                                  formatter={(v, name) => [fmtD2(v) + ' ' + unitLabel, name === 'ma' ? r.employee_name : t('efficiency.teamAvg')]}
                                  labelFormatter={(l) => l}
                                />
                                <Bar dataKey="ma" name={r.employee_name} fill={COLORS.violet} radius={[4, 4, 0, 0]} />
                                <Bar dataKey="team" name={t('efficiency.teamAvg')} fill={COLORS.gray} radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          {/* SOP info */}
                          {r.sop.packs > 0 && (
                            <div className="mt-3 flex items-center gap-4 text-[10px] text-[var(--text-secondary)]">
                              <span>{t('efficiency.sop')}: {fmt(r.sop.packs)} Packs, {fmt(r.sop.artikel)} {t('efficiency.articles')}, {fmtEur(r.sop.kosten)}</span>
                              <span className="text-[var(--text-tertiary)]">({t('efficiency.sopSeparate')})</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Trend Chart ── */}
      {subView === 'trend' && (
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {/* Granularity toggle */}
            <div className="flex bg-surface-inset rounded-lg p-0.5 border border-line-hover">
              {[
                { key: 'week', label: t('efficiency.week') },
                { key: 'month', label: t('efficiency.month') },
              ].map(g => (
                <button key={g.key} onClick={() => setGranularity(g.key)}
                  className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${
                    granularity === g.key ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-[var(--text-secondary)] hover:text-content-secondary'
                  }`}>
                  {g.label}
                </button>
              ))}
            </div>

            {/* Complexity filter */}
            <select value={trendKlasse} onChange={e => setTrendKlasse(e.target.value)}
              className="bg-surface-inset border border-line-hover rounded-lg px-2.5 py-1 text-xs text-content focus:outline-none">
              {klasseLabels.map(f => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>

            {/* Employee filter for trend */}
            <select value={trendEmployee} onChange={e => setTrendEmployee(e.target.value)}
              className="bg-surface-inset border border-line-hover rounded-lg px-2.5 py-1 text-xs text-content focus:outline-none max-w-[180px]">
              <option value="">{t('efficiency.teamAvg')}</option>
              {employees.map(e => <option key={e.name} value={e.name}>{e.name}</option>)}
            </select>
          </div>

          {trendData && trendData.length > 1 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-table)" />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} tickFormatter={fmtPeriod} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} />
                  <Tooltip
                    contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: 8, fontSize: 12 }}
                    labelFormatter={fmtPeriod}
                    formatter={(v, name) => [fmtD2(v) + ' ' + unitLabel, name === 'brutto_effizienz' ? (trendEmployee || t('efficiency.grossEff')) : t('efficiency.teamAvg')]}
                  />
                  <Line type="monotone" dataKey="brutto_effizienz" stroke={COLORS.violet} strokeWidth={2} dot={{ r: 3, fill: COLORS.violet }} name={trendEmployee || t('efficiency.grossEff')} />
                  {trendEmployee && <Line type="monotone" dataKey="team_avg" stroke={COLORS.gray} strokeWidth={2} strokeDasharray="6 3" dot={false} name={t('efficiency.teamAvg')} />}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('efficiency.noData')}</div>
          )}
        </div>
      )}

      {/* ── Compare Mode ── */}
      {subView === 'compare' && (
        <div>
          <div className="text-xs text-[var(--text-secondary)] mb-3">{t('efficiency.selectEmployees')}</div>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {employees.filter(e => !e.is_shared).map((e, i) => (
              <button key={e.name} onClick={() => toggleCompare(e.name)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors border ${
                  compareSelected.includes(e.name)
                    ? 'border-primary-500 bg-primary-500/10 text-primary-500'
                    : 'border-line text-[var(--text-secondary)] hover:border-line-hover'
                }`}>
                {e.name}
                {compareSelected.includes(e.name) && (
                  <span className="ml-1 w-2 h-2 rounded-full inline-block" style={{ background: COMPARE_COLORS[compareSelected.indexOf(e.name)] || COLORS.gray }} />
                )}
              </button>
            ))}
          </div>

          {compareData && compareData.length >= 2 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[1, 2, 3, 4].map(k => {
                  const entry = { name: klasseChartNames[k] };
                  for (const emp of compareData) {
                    const kd = emp.klassen.find(kl => kl.klasse === k);
                    entry[emp.name] = kd ? kd.brutto_eff : 0;
                  }
                  if (compareData[0]?.klassen) {
                    const kd = compareData[0].klassen.find(kl => kl.klasse === k);
                    entry['team_avg'] = kd ? kd.team_avg : 0;
                  }
                  return entry;
                })} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-table)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} />
                  <Tooltip
                    contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => [fmtD2(v) + ' ' + unitLabel]}
                  />
                  <Legend />
                  {compareData.map((emp, i) => (
                    <Bar key={emp.name} dataKey={emp.name} fill={COMPARE_COLORS[i] || COLORS.gray} radius={[4, 4, 0, 0]} />
                  ))}
                  <Bar dataKey="team_avg" name={t('efficiency.teamAvg')} fill={COLORS.gray} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">
              {compareSelected.length < 2 ? t('efficiency.selectEmployees') : t('efficiency.noData')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
